import React from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import './BayModal.scss';

const DEFAULT_MODAL_MAX_HEIGHT = 400;
interface BayModalProps {
	children?: React.ReactNode;
	buttonText?: string;
	bodyClass?: string;
	submitClass?: string;
	cancelClass?: string;
	cancelText?: string;
	hideHeader?: boolean;
	hideFooter?: boolean;
	hideCancel?: boolean;
	hideSubmit?: boolean;
	fullscreen?: true | string;
	closeButton?: boolean;
	id?: string | number;
	onHideFunc?: (_value?: boolean) => void;
	show?: boolean;
	submitFunc?: () => void;
	variant?: string;
	backdrop?: boolean | 'static';
	title?: React.ReactNode;
	scrollable?: boolean;
	footer?: React.ReactNode;
	footerClassName?: string;
	headerClassName?: string;
	disabled?: boolean;
	disableCancel?: boolean;
	size?: 'sm' | 'lg' | 'xl';
	grayBackground?: boolean;
	noPadding?: boolean;
	height?: string | number;
	style?: React.CSSProperties;
	centered?: boolean;
	className?: string;
	backdropClassName?: string;
	/**
	 * Fired after the close transition finishes. Needed by anything that mounts
	 * this modal imperatively and has to tear its own root down only once the
	 * exit animation has played -- see BayDialog.
	 */
	onExited?: () => void;
	/**
	 * Keep focus trapped inside the modal. Defaults to true; only opt out when a
	 * non-React dialog (e.g. a jQuery modal) is layered on top and needs focus.
	 */
	enforceFocus?: boolean;
	t?: (_key: string, _defaultVal?: string) => string;
	/** @deprecated Use 't' prop instead */
	translate?: (_key: string, _defaultVal?: string) => string;
}

const BayModal = ({
	children,
	buttonText,
	bodyClass='',
	submitClass,
	cancelClass,
	cancelText,
	disableCancel,
	hideHeader,
	hideFooter,
	hideCancel,
	hideSubmit,
	fullscreen,
	closeButton,
	id,
	className,
	onHideFunc = () => {},
	show = false,
	submitFunc,
	variant,
	backdrop,
	title,
	scrollable,
	footer,
	footerClassName,
	headerClassName,
	disabled,
	size,
	grayBackground,
	noPadding,
	height,
	style,
	centered = false,
	backdropClassName = 'bay_modal_backdrop',
	enforceFocus = true,
	t,
	translate,
	...restProps
}: BayModalProps) => {
	const innerTranslate = useTranslation();

	// Priority: t prop > translate prop (deprecated) > useTranslation hook
	const translateFunc = t || translate || innerTranslate.t;

	const showCancelConfirm = () => {
		onHideFunc(false);
	};

	return (
		<Modal
			backdrop={backdrop}
			backdropClassName={backdropClassName}
			centered={centered}
			enforceFocus={enforceFocus}
			// clsx, NOT a bare string. `className` used to fall through to
			// `restProps` and be spread onto <Modal> AFTER this line, so any caller
			// passing one REPLACED `bay-modal` and silently lost every rule in
			// BayModal.scss. BayDialog passes `confirm-dialog`, so it had no
			// `.bay-modal` styling at all. React keeps the later prop, not the
			// merged value -- so a hardcoded class must always be merged, never
			// followed by a spread that can carry the same key.
			className={clsx('bay-modal', className)}
			fullscreen={fullscreen}
			id={id ? String(id) : 'bay_generic_modal'}
			scrollable={scrollable}
			show={show}
			size={size}
			style={style}
			onHide={onHideFunc}
			{...restProps}
		>
			{!hideHeader && (
				// headerClassName is the caller's override hook for header padding/spacing
				<Modal.Header closeButton={closeButton} className={clsx(headerClassName)}>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
			)}
			<Modal.Body
				className={clsx(bodyClass,{
					'bg-25': grayBackground,
					'no-footer': hideFooter,
					'p-0': noPadding,
				})}
				style={{ maxHeight: !fullscreen ? (height ?? DEFAULT_MODAL_MAX_HEIGHT) : '100%' }}
			>
				{children}
			</Modal.Body>
			{!hideFooter && (
				<Modal.Footer
					className={clsx('d-flex', {
						'justify-content-end': hideCancel || hideSubmit,
						'justify-content-between': !(hideCancel || hideSubmit),
						'border-0': hideCancel && hideSubmit,
					}, footerClassName)}
				>
					{footer ? (
						<>{footer}</>
					) : (
						<>
							{!hideCancel && (
								<Button
									className={cancelClass ? `${cancelClass}` : 'cancel-button'}
									disabled={disableCancel}
									variant="outline-secondary"
									onClick={() => showCancelConfirm()}
								>
									{cancelText ? cancelText : translateFunc('cancel')}
								</Button>
							)}
							{!hideSubmit && (
								<Button
									className={submitClass ? `${submitClass} ms-3` : 'ms-3'}
									disabled={disabled}
									variant={variant ? variant : 'primary'}
									onClick={submitFunc ? submitFunc : () => void 0}
								>
									{buttonText ? buttonText : translateFunc('submit')}
								</Button>
							)}
						</>
					)}
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default BayModal;
